import React from 'react';
import { DefaultEditors, ProductType, TagType, Template } from '@dev/ee-api/ee-api-models';
import { push as Push, replace as Replace } from 'redux-first-history';
import { connect, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { TemplateList as TemplateListModule } from '@dev/ee-modules';
import { HistoryBackActionContext, useHistoryBackAction } from '@/services/hooks/useHistoryBackAction';

import { parseLegacyUrl } from '@/services/tools/parseLegacyUrl';
import { TemplatesPaths } from '@/services/dictionaries/Paths';
import { HideDialog, ShowDialog, UpdateDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { templateTagDeleteStart, templateTagListStart } from '@/store/templateTagList/actions';
import {
    AddTemplateStart,
    StartLoadingTemplateParams,
    StartLoadingTemplates,
    StartUpdatingTemplateParams,
    templateArrayDeleteStart,
    templateDeleteStart,
    TemplatePreviewStart,
    templateUpdateStart,
    ShareTemplateStart,
    CreateSharedTemplateStart,
} from '@/store/templateList/actions';
import { GetDashboardSettings, SetDefaultEditor } from '@/store/dashboardSettings/actions';
import { getCampaignsStore, SetCampaignCreatorOpen } from '@/store/campaigns/actions';
import { LoadTemplate } from '@/store/raweditor/actions';
import { ApplicationState as State } from '@/store';
import { useDashboardElementVisibility } from '@/services/hooks/useDashboardElementVisibility';
import { addParamToUrl } from '@/services/tools/handleUrlParams';
import Props from './TemplateListInterface';
import { TemplateListProps } from './TemplateListInterface';
import { StartCampaignCreator } from '../CampaignCreator/Start';

declare const window: any;
declare const ee: any;

const mapState = (state: State, ownProps: TemplateListProps): TemplateListProps => {
    const getApiPath = (): string => {
        if (!state.config.isStandalone && window.ee) {
            return ee.api.path;
        }

        return APIPATH;
    };

    return {
        ...ownProps,
        config: state.config,
        account: state.account,
        dashboardSettings: state.dashboardSettings,
        browser: state.browser,
        templates: state.templateList,
        tags: state.templateTagList,
        rawEditor: state.rawEditor,
        APIPATH: getApiPath(),
        isMarketing: true,
        isShareable: true,
    };
};

interface DispatchProps {
    StartLoadingTemplates(params: StartLoadingTemplateParams): void;
    templateTagListStart: (type: TagType) => void;
    templateTagDeleteStart: (name: string) => void;
    HideDialog: () => void;
    ShowDialog: (params: ShowDialogParams) => void;
    UpdateDialog: (params: ShowDialogParams) => void;
    templateDeleteStart: (name: string, params: StartLoadingTemplateParams) => void;
    templateArrayDeleteStart: (templatesToDelete: string[], params: StartLoadingTemplateParams) => void;
    TemplatePreviewStart: (templateName: string, ispublic?: boolean) => void;
    templateUpdateStart(params: StartUpdatingTemplateParams): void;
    AddTemplateStart: (template: Template) => void;
    LoadTemplate: (name: string, loadDefault: boolean, isPublic?: boolean) => void;
    Push: (url) => void;
    Replace(url: string | Record<string, string>): void;
    ShareTemplateStart(name: string): void;
    CreateSharedTemplateStart(shareIdentifier: string): void;
    SetCampaignCreatorOpen(isCampaignCreatorOpen: boolean, contactsList: string, templateName: string): void;
    GetDashboardSettings: () => void;
    SetDefaultEditor: (defaultEditor: DefaultEditors) => void;
}
const dispatchProps = {
    StartLoadingTemplates,
    templateTagListStart,
    templateTagDeleteStart,
    ShowDialog,
    HideDialog,
    UpdateDialog,
    templateDeleteStart,
    templateArrayDeleteStart,
    TemplatePreviewStart,
    templateUpdateStart,
    AddTemplateStart,
    LoadTemplate,
    Push,
    Replace,
    ShareTemplateStart,
    CreateSharedTemplateStart,
    SetCampaignCreatorOpen,
    GetDashboardSettings,
    SetDefaultEditor
};

type ExtendedProps = TemplateListProps & DispatchProps;

const TemplateList = (props: ExtendedProps): JSX.Element => {
    const { t } = useTranslation();
    const { isCampaignCreatorOpen } = useSelector(getCampaignsStore);
    const isSavedRef = React.useRef(true);
    const { editors: { isHtmlEditorVisible } } = useDashboardElementVisibility();
 
    const historyBackAction = useHistoryBackAction({
        isSavedRef
    });
    
    const setUrlParams = (param: Record<string, any>): void => {
        const { Replace } = props;
        const urlParams = addParamToUrl(param);
        Replace({ search: urlParams });
    };

    const { Campaingcreator } = TemplatesPaths;
    const paths = {
        ...TemplatesPaths,
        //LegacyDragDrop: TemplatesPaths.TemplateEditor,
        Campaingcreator: parseLegacyUrl(Campaingcreator, props.config.isStandalone),
    };
    const isCampaignScreenAvailable = [ProductType.Marketing, ProductType.CreatorSuite].includes(props.account?.GeneralInfo?.ProductType);

    return (
        
        <HistoryBackActionContext.Provider value={historyBackAction}>
            <TemplateListModule
                paths={paths}
                t={t}
                retriveUrlParamsToAdd={setUrlParams}
                {...props}
                isCampaignScreenAvailable={isCampaignScreenAvailable}
                isHtmlEditorVisible={isHtmlEditorVisible}
            />
            {isCampaignCreatorOpen && <StartCampaignCreator />}
        </HistoryBackActionContext.Provider>
        
    );
};

export default connect<Props, DispatchProps>(mapState, dispatchProps)(TemplateList);
